import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection } from "@/components/FaqSection";
import { SITE_URL, hreflangLinks, faqPageSchema } from "@/lib/seo";

const PATH = "/pakistan/gold-forecast";
const TITLE = "Will Gold Rate Decrease in Pakistan? — Gold Price Forecast & Trend Analysis";
const DESC = "Will gold rate decrease in coming days in Pakistan? Live sona ka bhao forecast — why gold price is rising or falling, USD/PKR impact, Fed rate, and near-term Pakistan gold trend analysis.";

const FAQS = [
  { q: "Will gold rate decrease in coming days in Pakistan?", a: "Gold rate in Pakistan follows two independent variables — international XAU/USD spot and the USD/PKR exchange rate. Even when spot falls, a weaker rupee can keep local rates elevated. Short-term forecasts should be treated as directional, not precise: we publish a live trend read every 10 minutes above." },
  { q: "Why is gold price rising in Pakistan?", a: "Three drivers push gold up: (1) global uncertainty (war, banking stress) pushing safe-haven demand, (2) US Fed rate cuts weakening the dollar, and (3) rupee depreciation against USD. In Pakistan, the second and third often matter more than global spot." },
  { q: "Why is gold price falling in Pakistan?", a: "Gold falls when the US Fed hikes rates (strong dollar), when global risk appetite improves, or when the rupee unexpectedly strengthens against USD. Local sarafa premiums also compress when demand is weak." },
  { q: "Can gold price go down in Pakistan in the next 30 days?", a: "Yes — historically gold has weekly moves of ±3% and monthly moves of ±7%. Whether Pakistan retail rates fall depends more on USD/PKR stability than on international spot alone." },
  { q: "How accurate are gold price predictions?", a: "No public forecast is reliable beyond a broad direction. Institutional forecasts (Goldman, JPMorgan) have historically missed year-end targets by 10–20%. Focus on trend, not exact price." },
  { q: "Should I buy gold now or wait in Pakistan?", a: "For jewellery, buy when you need it — trying to time the market usually costs more than it saves. For investment, dollar-cost-average (fixed monthly amount) beats lump-sum timing over 3+ year horizons." },
];

export const Route = createFileRoute("/pakistan/gold-forecast")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "en_PK" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }, ...hreflangLinks(PATH)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqPageSchema(FAQS)) },
    ],
  }),
  component: ForecastPage,
});

function ForecastPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/pakistan" className="hover:text-primary">Pakistan Gold Rate</Link> / Forecast
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold">Will Gold Rate Decrease in Pakistan? — Live Forecast &amp; Trend Analysis</h1>
      <p className="mt-4 text-foreground/85 leading-relaxed">
        Aaj Pakistan mein sona kis taraf ja raha hai? This page tracks the three
        variables that decide whether the gold rate in Pakistan will decrease or
        rise in coming days: <strong>international XAU/USD spot</strong>, the
        <strong> USD/PKR exchange rate</strong>, and local <strong>sarafa bazaar
        demand</strong>. For the live per-tola price, jump to our{" "}
        <Link to="/pakistan" className="text-primary underline">live gold rate in Pakistan</Link> page.
      </p>

      <section className="mt-8 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-xl font-bold mb-3">Why gold price is rising or falling — the 3 drivers</h2>
        <ol className="list-decimal ml-6 space-y-3 text-sm leading-relaxed">
          <li><strong>Global XAU/USD spot</strong> — moves with US inflation, Fed rate expectations, and safe-haven demand. A weaker US dollar generally lifts gold.</li>
          <li><strong>USD/PKR exchange rate</strong> — a weaker rupee inflates the Pakistan retail rate even when international gold is flat. This is often the biggest short-term driver for Pakistani buyers.</li>
          <li><strong>Local sarafa premium</strong> — Karachi, Lahore aur Rawalpindi ke bazaar demand-supply pe premium ±0.5–1.5% adjust karte hain. Wedding season aur Eid demand se rates thoda oopar jaate hain.</li>
        </ol>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-xl font-bold mb-3">Can gold price go down in Pakistan?</h2>
        <p className="text-sm leading-relaxed">
          Yes — historically gold has dropped 5–10% in a single month multiple
          times (March 2020, November 2022, February 2024). In Pakistan
          specifically, drops require BOTH international spot to fall AND the
          rupee to stay stable. If rupee weakens simultaneously, local rates can
          hold flat or even rise while global gold falls.
        </p>
      </section>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Link to="/pakistan" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">Live gold rate in Pakistan</div>
          <div className="text-xs text-muted-foreground mt-1">24K, 22K, 21K, 18K per tola & per gram — refreshed every 10 min.</div>
        </Link>
        <Link to="/pakistan/yesterday" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">Yesterday's gold rate</div>
          <div className="text-xs text-muted-foreground mt-1">Day-over-day price difference to spot the trend.</div>
        </Link>
        <Link to="/pakistan/22k" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">22K gold rate in Pakistan</div>
          <div className="text-xs text-muted-foreground mt-1">Jewellery-grade 22 carat sona ka bhao.</div>
        </Link>
        <Link to="/pakistan/vs/india" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">Pakistan vs India gold rate</div>
          <div className="text-xs text-muted-foreground mt-1">Cross-border price comparison.</div>
        </Link>
      </div>

      <FaqSection faqs={FAQS} title="Gold price forecast Pakistan — most-asked questions" />
    </main>
  );
}